import { useCallback, useEffect, useState } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { Ad, type AdType } from "@models/Ad.model";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";

const AD_TYPES: AdType[] = ["video", "image", "document", "form", "email", "sms", "container"];

type EditableFields = {
    name: string;
    type: AdType;
    subtype: string;
    is_default: boolean;
    is_template: boolean;
    parent_id: string;
};

function adToEditable(ad: Ad): EditableFields {
    return {
        name: ad.name,
        type: ad.type,
        subtype: ad.subtype ?? "",
        is_default: ad.is_default,
        is_template: ad.is_template,
        parent_id: ad.parent_id != null ? String(ad.parent_id) : "",
    };
}

interface DebugNodeProps {
    ad: Ad;
    depth: number;
    saving: number | null;
    deleting: number | null;
    onSave: (adId: number, fields: EditableFields) => void;
    onDelete: (adId: number) => void;
}

function DebugNode({ ad, depth, saving, deleting, onSave, onDelete }: DebugNodeProps) {
    const [expanded, setExpanded] = useState(depth === 0);
    const [editing, setEditing] = useState(false);
    const [fields, setFields] = useState<EditableFields>(adToEditable(ad));

    useEffect(() => {
        setFields(adToEditable(ad));
    }, [ad]);

    const isSaving = saving === ad.id;
    const isDeleting = deleting === ad.id;
    const isBusy = isSaving || isDeleting;
    const hasChildren = ad.children && ad.children.length > 0;

    const handleSave = () => {
        onSave(ad.id, fields);
        setEditing(false);
    };

    const handleCancel = () => {
        setFields(adToEditable(ad));
        setEditing(false);
    };

    return (
        <div style={{ marginLeft: depth > 0 ? 16 : 0 }}>
            <div
                style={{
                    border: "1px solid #dee2e6",
                    borderRadius: 6,
                    padding: "0.5rem 0.625rem",
                    marginBottom: 4,
                    background: editing ? "#f8f9ff" : "#fff",
                }}
            >
                {/* Header row */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: editing ? 8 : 0 }}>
                    {hasChildren && (
                        <button
                            type="button"
                            onClick={() => setExpanded(!expanded)}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "0.72rem",
                                color: "#6c7585",
                                padding: 0,
                                width: 16,
                            }}
                        >
                            {expanded ? "\u25BC" : "\u25B6"}
                        </button>
                    )}
                    {!hasChildren && <span style={{ width: 16, display: "inline-block" }} />}

                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#2f3440", flex: 1 }}>
                        {ad.name || `Ad ${ad.id}`}
                    </span>

                    <span style={{ fontSize: "0.7rem", color: "#6c7585" }}>
                        id: {ad.id} &middot; {ad.type}{ad.subtype ? `/${ad.subtype}` : ""}
                    </span>

                    {!editing && (
                        <div style={{ display: "flex", gap: 4 }}>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                style={{ fontSize: "0.7rem", padding: "0.15rem 0.5rem" }}
                                onClick={() => setEditing(true)}
                                disabled={isBusy}
                            >
                                Edit
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                style={{ fontSize: "0.7rem", padding: "0.15rem 0.5rem" }}
                                onClick={() => { if (confirm(`Delete "${ad.name || `Ad ${ad.id}`}"?`)) onDelete(ad.id); }}
                                disabled={isBusy}
                            >
                                {isDeleting ? <Spinner size="sm" animation="border" /> : "Delete"}
                            </button>
                        </div>
                    )}
                </div>

                {/* Editable fields */}
                {editing && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                            <Form.Group>
                                <Form.Label style={{ fontSize: "0.7rem", marginBottom: 2 }}>name</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={fields.name}
                                    onChange={(e) => setFields({ ...fields, name: e.target.value })}
                                    style={{ fontSize: "0.78rem" }}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label style={{ fontSize: "0.7rem", marginBottom: 2 }}>type</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={fields.type}
                                    onChange={(e) => setFields({ ...fields, type: e.target.value as AdType })}
                                    style={{ fontSize: "0.78rem" }}
                                >
                                    {AD_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label style={{ fontSize: "0.7rem", marginBottom: 2 }}>subtype</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={fields.subtype}
                                    onChange={(e) => setFields({ ...fields, subtype: e.target.value as AdType })}
                                    style={{ fontSize: "0.78rem" }}
                                >
                                    <option value="">—</option>
                                    {AD_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label style={{ fontSize: "0.7rem", marginBottom: 2 }}>parent_id</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={fields.parent_id}
                                    onChange={(e) => setFields({ ...fields, parent_id: e.target.value })}
                                    placeholder="null"
                                    style={{ fontSize: "0.78rem" }}
                                />
                            </Form.Group>
                        </div>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <Form.Check
                                type="checkbox"
                                label="is_default"
                                checked={fields.is_default}
                                onChange={(e) => setFields({ ...fields, is_default: e.target.checked })}
                                style={{ fontSize: "0.78rem" }}
                            />
                            <Form.Check
                                type="checkbox"
                                label="is_template"
                                checked={fields.is_template}
                                onChange={(e) => setFields({ ...fields, is_template: e.target.checked })}
                                style={{ fontSize: "0.78rem" }}
                            />
                            <span style={{ flex: 1 }} />
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                style={{ fontSize: "0.7rem", padding: "0.15rem 0.5rem" }}
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: "0.7rem", padding: "0.15rem 0.5rem" }}
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? <Spinner size="sm" animation="border" /> : "Save"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Children */}
            {expanded && hasChildren && (
                <div>
                    {ad.children.map((child) => (
                        <DebugNode
                            key={child.id}
                            ad={child}
                            depth={depth + 1}
                            saving={saving}
                            deleting={deleting}
                            onSave={onSave}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

interface DebugAdTreeModalProps {
    show: boolean;
    onClose: () => void;
    rootAd: Ad;
    onAdUpdated: () => void;
}

export default function DebugAdTreeModal({ show, onClose, rootAd, onAdUpdated }: DebugAdTreeModalProps) {
    const [saving, setSaving] = useState<number | null>(null);
    const [deleting, setDeleting] = useState<number | null>(null);

    const handleSave = useCallback(async (adId: number, fields: EditableFields) => {
        setSaving(adId);
        try {
            const ccService = new ContentCatalogService();
            const payload: Record<string, unknown> = {
                name: fields.name,
                type: fields.type,
                subtype: fields.subtype || undefined,
                is_default: fields.is_default,
                is_template: fields.is_template,
                parent_id: fields.parent_id ? Number(fields.parent_id) : null,
            };
            await ccService.updateAd(adId, payload as any);
            onAdUpdated();
        } catch (error) {
            console.error("Debug: failed to update ad", error);
        } finally {
            setSaving(null);
        }
    }, [onAdUpdated]);

    const handleDelete = useCallback(async (adId: number) => {
        setDeleting(adId);
        try {
            const ccService = new ContentCatalogService();
            await ccService.deleteAd(adId);
            onAdUpdated();
        } catch (error) {
            console.error("Debug: failed to delete ad", error);
        } finally {
            setDeleting(null);
        }
    }, [onAdUpdated]);

    return (
        <Modal
            show={show}
            onHide={onClose}
            size="lg"
            className="ceh-nested-modal"
            backdropClassName="ceh-nested-backdrop"
            style={{ zIndex: 1400 }}
        >
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: "1rem" }}>Debug: Ad Tree</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                <DebugNode
                    ad={rootAd}
                    depth={0}
                    saving={saving}
                    deleting={deleting}
                    onSave={(adId, fields) => { void handleSave(adId, fields); }}
                    onDelete={(adId) => { void handleDelete(adId); }}
                />
            </Modal.Body>
        </Modal>
    );
}
